/**
 * RequestMergePanel - Entity-specific merge panel for Requests
 * 
 * Provides a familiar UI for both source items (view mode) and merged result (edit mode).
 * 
 * This replaces the generic dynamic merge UI when entityType === 'request',
 * giving users a clear understanding of what data is being kept vs lost.
 * 
 * Props:
 * - sourceItems: array of request objects fetched from DB
 * - selectedItems: { [idx]: boolean } - which source items are included
 * - expandedItems: { [idx]: boolean } - which source items are expanded
 * - onToggleSelect: (idx) => void
 * - onToggleExpand: (idx) => void
 * - formData: merged request form data
 * - onFormChange: (field, value) => void
 * - productId: string
 * - errors: { [field]: string } - validation errors
 */

import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { useSafeMode } from '../../contexts/SafeModeContext';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Plus,
  Minus,
  AlertTriangle,
  Bug,
  Sparkles,
  Layers,
  Rocket,
  Target,
  Users,
} from 'lucide-react';

// Import reusable form components
import RequestOverviewForm, { 
  REQUEST_TYPE_CONFIG,
  REQUEST_REQUIRED_FIELDS,
  REQUEST_PRIORITY_OPTIONS,
} from '../request/RequestOverviewForm';

// =============================================================================
// SOURCE REQUEST CARD (Left Panel)
// =============================================================================

const SourceRequestCard = ({
  request,
  index,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  colors,
}) => {
  const typeConfig = REQUEST_TYPE_CONFIG[request.type] || REQUEST_TYPE_CONFIG.feature;
  const TypeIcon = typeConfig.icon;
  const priorityLevel = request.request_data?.priority_level || 'Medium';
  const priorityColor = REQUEST_PRIORITY_OPTIONS.find(p => p.value === priorityLevel)?.color || 'text-gray-500';

  return (
    <div 
      className={cn(
        "rounded-lg border overflow-hidden transition-all",
        isSelected 
          ? "border-blue-300 dark:border-blue-600 bg-white dark:bg-gray-800" 
          : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60"
      )}
      data-testid={`source-request-${index}`}
    >
      {/* Header - Always Visible */}
      <div 
        className={cn(
          "flex items-center gap-3 p-3 cursor-pointer",
          "hover:bg-gray-50 dark:hover:bg-gray-700/50"
        )}
        onClick={onToggleExpand}
      >
        {/* Selection Checkbox */}
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            className="border-gray-400"
          />
        </div>
        
        {/* Expand/Collapse Icon */}
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
        
        {/* Type Icon */}
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          isSelected ? typeConfig.color : "bg-gray-200 dark:bg-gray-700"
        )}>
          <TypeIcon className={cn("w-4 h-4", isSelected ? "text-white" : "text-gray-500")} />
        </div>
        
        {/* Request Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              "font-medium truncate",
              isSelected ? "text-gray-900 dark:text-gray-100" : "text-gray-500 line-through"
            )}>
              {request.title || 'Untitled Request'}
            </span>
            {!isSelected && (
              <Badge variant="outline" className="text-xs text-red-500 border-red-300">
                Excluded
              </Badge>
            )}
          </div>
          {!isExpanded && (
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
              <span className={cn("capitalize", priorityColor)}>{priorityLevel}</span>
              <span>•</span>
              <span>{typeConfig.label}</span>
              {request.stage && (
                <>
                  <span>•</span>
                  <span className="capitalize">{request.stage}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className={cn("border-t p-3", colors.borderPrimary)}>
          <RequestOverviewForm
            formData={request}
            isEditing={false}
            isDisabled={true}
            colors={colors}
            mode="view"
            showStatus={true}
          />
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MERGE DIFF SUMMARY
// =============================================================================

const MergeDiffSummary = ({ sourceItems, selectedItems, colors }) => {
  // Calculate what's being kept vs lost
  const selectedRequests = sourceItems.filter((_, idx) => selectedItems[idx]);
  const excludedRequests = sourceItems.filter((_, idx) => !selectedItems[idx]);

  if (excludedRequests.length === 0) {
    return null; // No diff to show
  }

  return (
    <div className={cn("p-3 rounded-lg border mb-4", "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700")}>
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-amber-800 dark:text-amber-200 text-sm">
          Merge Impact Summary
        </span>
      </div>
      <div className="space-y-1 text-xs">
        {excludedRequests.length > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedRequests.length} request(s) excluded: {excludedRequests.map(r => `"${r.title}"`).join(', ')}
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 pt-1 border-t border-amber-300 dark:border-amber-600 mt-2">
          <Plus className="w-3 h-3 text-green-500" />
          <span className="text-green-700 dark:text-green-300">
            Merged request will combine content from {selectedRequests.length} selected request(s)
          </span>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MERGED REQUEST FORM (Right Panel)
// =============================================================================

const MergedRequestForm = ({
  formData,
  onFormChange,
  colors,
  errors = {},
}) => {
  const handleChange = (field, value) => {
    onFormChange(field, value);
  };

  // Check if there are errors in overview fields
  const hasOverviewErrors = Object.keys(errors).some(k => REQUEST_REQUIRED_FIELDS.includes(k));

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 pb-2 mb-2 border-b border-gray-200 dark:border-gray-700">
        <h4 className={cn("font-medium", colors.textPrimary)}>
          Overview {hasOverviewErrors && <span className="text-red-500">⚠️</span>}
        </h4>
        <p className={cn("text-xs", colors.textMuted)}>
          Edit the combined request details
        </p>
      </div>
      
      {/* Form Content */}
      <div className="flex-1 overflow-y-auto">
        <RequestOverviewForm
          formData={formData}
          onChange={handleChange}
          isEditing={true}
          isDisabled={false}
          colors={colors}
          errors={errors}
          mode="create"
          showStatus={false}
        />
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const RequestMergePanel = ({
  sourceItems,
  selectedItems,
  expandedItems,
  onToggleSelect,
  onToggleExpand,
  formData,
  onFormChange,
  productId,
  errors = {},
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Selected count
  const selectedCount = Object.values(selectedItems).filter(Boolean).length;

  return (
    <div className="grid grid-cols-2 h-full divide-x divide-gray-200 dark:divide-gray-700">
      {/* Left Panel: Source Requests */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Source Requests
            </h3>
            <Badge variant="outline">
              {selectedCount} of {sourceItems.length} selected
            </Badge>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Select requests to include in the merge. Expand to review data.
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {sourceItems.map((request, idx) => (
            <SourceRequestCard
              key={request.request_id || idx}
              request={request}
              index={idx}
              isSelected={selectedItems[idx]}
              isExpanded={expandedItems[idx]}
              onToggleSelect={() => onToggleSelect(idx)}
              onToggleExpand={() => onToggleExpand(idx)}
              colors={colors}
            />
          ))}
        </div>
      </div>
      
      {/* Right Panel: Merged Request Form */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">
            Merged Result
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Edit the combined request before applying
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {/* Diff Summary */}
          <MergeDiffSummary
            sourceItems={sourceItems}
            selectedItems={selectedItems}
            colors={colors}
          />
          
          {/* Merged Form */}
          <MergedRequestForm
            formData={formData}
            onFormChange={onFormChange}
            colors={colors}
            errors={errors}
          />
        </div>
      </div>
    </div>
  );
};

export default RequestMergePanel;
